import React, { useEffect, useRef, useState } from "react";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { providerLabel } from "../../lib/industry";

const EMPTY = { name: "", email: "", password: "", role: "stylist", phone: "", bio: "", specialties: "", avatar: "" };
const ROLE_STYLE = { owner: "bg-primary/10 text-primary", receptionist: "bg-accent/20 text-accent-foreground", stylist: "bg-secondary text-secondary-foreground" };

export default function Team() {
  const { user, salon } = useAuth();
  const roleLabel = (r) => (r === "stylist" ? providerLabel(salon?.business_type) : r === "owner" ? "Owner" : "Receptionist");
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const avatarRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/staff/avatar-upload", fd);
      setForm((f) => ({ ...f, avatar: data.url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingAvatar(false); if (avatarRef.current) avatarRef.current.value = ""; }
  };
  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, email: s.email, password: "", role: s.role, phone: s.phone || "", bio: s.bio || "", specialties: (s.specialties || []).join(", "), avatar: s.avatar || "" }); setOpen(true); };
  const save = async () => {
    try {
      const p = { ...form, specialties: form.specialties.split(",").map((x) => x.trim()).filter(Boolean) };
      if (editing) await api.put(`/staff/${editing.id}`, p); else await api.post("/staff", p);
      toast.success("Team member saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <CrmLayout title="Team" actions={<Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="add-staff-btn" onClick={openNew}><Plus size={16} className="mr-1" /> Add member</Button>}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => (
          <div key={s.id} className="bg-card border border-border rounded-lg p-5 flex gap-4" data-testid={`staff-card-${s.id}`}>
            {s.avatar ? <img src={s.avatar} alt={s.name} className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center font-display text-lg font-bold text-muted-foreground">{s.name[0]}</div>}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0"><h3 className="font-semibold truncate">{s.name}</h3><Badge className={`${ROLE_STYLE[s.role]} mt-0.5`}>{roleLabel(s.role)}</Badge></div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" data-testid={`edit-staff-${s.id}`} onClick={() => openEdit(s)}><Pencil size={14} /></Button>
                  {s.id !== user?.id && <Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-staff-${s.id}`} onClick={() => remove(s.id)}><Trash2 size={14} /></Button>}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.bio}</p>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl font-semibold">{editing ? "Edit" : "Add"} team member</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="staff-name" className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="stylist">{providerLabel(salon?.business_type)}</SelectItem><SelectItem value="receptionist">Receptionist</SelectItem><SelectItem value="owner">Owner</SelectItem></SelectContent>
              </Select></div>
            <div><Label>Email</Label><Input data-testid="staff-email" className="mt-1" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={!!editing} /></div>
            <div><Label>Password {editing && <span className="text-muted-foreground text-xs">(blank = keep)</span>}</Label><Input data-testid="staff-password" type="password" className="mt-1" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Specialties (comma separated)</Label><Input data-testid="staff-specialties" className="mt-1" value={form.specialties} onChange={(e) => setForm((f) => ({ ...f, specialties: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Bio</Label><Textarea data-testid="staff-bio" className="mt-1" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} /></div>
            <div className="col-span-2">
              <Label>Profile picture</Label>
              <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="staff-avatar-file" onChange={uploadAvatar} />
              <div className="mt-1.5 flex items-center gap-3">
                {form.avatar
                  ? <img src={form.avatar} alt="Avatar" className="w-14 h-14 rounded-full object-cover border border-border" />
                  : <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center font-display text-lg text-muted-foreground">{(form.name || "?")[0]}</div>}
                <Button type="button" variant="outline" size="sm" data-testid="staff-avatar-upload" disabled={uploadingAvatar} onClick={() => avatarRef.current?.click()}>{uploadingAvatar ? "Uploading…" : "Upload photo"}</Button>
                {form.avatar && <Button type="button" variant="ghost" size="sm" className="text-destructive" data-testid="staff-avatar-remove" onClick={() => setForm((f) => ({ ...f, avatar: "" }))}>Remove</Button>}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="staff-save" onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CrmLayout>
  );
}
